import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SelectField } from "../../components/SelectField";
import { Panel } from "../admin/fields";
import { KeyField, SaveButton } from "../admin/integrationForm";

const KEYS = ["SMTP_HOST", "SMTP_PORT", "SMTP_SECURITY", "SMTP_USERNAME", "SMTP_PASSWORD", "SMTP_FROM_EMAIL", "SMTP_FROM_NAME"];
const toForm = (d) => Object.fromEntries(KEYS.map((k) => [k, d[k] || ""]));

const TestSend = () => {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      toast.success(`Test email sent to ${(await api.post("/platform/smtp/test", { to: to.trim() })).data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 flex flex-wrap items-end gap-3">
      <label className="field min-w-[260px] flex-1"><span className="field-label">Send a test email to</span>
        <input type="email" className="field-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@hostechsolutions.com" data-testid="system-smtp-test-to" />
      </label>
      <button type="button" className="btn-outline" onClick={send} disabled={busy || !to} data-testid="system-smtp-test-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send test
      </button>
    </div>
  );
};

export const SystemSmtpPanel = () => {
  const { data } = useQuery({ queryKey: ["system-smtp"], queryFn: () => api.get("/platform/smtp").then((r) => r.data) });
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);
  const save = async (clear = false) => {
    setBusy(true);
    try {
      const saved = (await api.put("/platform/smtp", { values: form, clear })).data;
      qc.setQueryData(["system-smtp"], saved);
      setForm(toForm(saved));
      toast.success(clear ? "System mail server removed" : "System mail server saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!form) return null;
  const p = { form, set: (x) => setForm((f) => ({ ...f, ...x })) };
  return (
    <Panel title="System mail server (SMTP)" hint="Default sender for every hotel that has not set up its own mail server, and for welcome and password-reset emails. If it fails, emails go out through the built-in service.">
      <p className={`mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${data.configured ? "bg-brand-light text-brand" : "bg-gold/15 text-ink"}`} data-testid="system-smtp-status">
        <Mail className="h-3.5 w-3.5" />{data.configured ? "Active: hotels without their own server send from here" : "Not set: the built-in email service is used"}
      </p>
      <div className="grid gap-5 md:grid-cols-2">
        <KeyField label="SMTP server" name="SMTP_HOST" hint="mail.hostechsolutions.com" {...p} testid="system-smtp-host" />
        <SelectField label="Security" value={form.SMTP_SECURITY || "starttls"} testid="system-smtp-security" onChange={(v) => p.set({ SMTP_SECURITY: v })}
          options={[{ value: "starttls", label: "STARTTLS (usually port 587)" }, { value: "ssl", label: "SSL/TLS (usually port 465)" }, { value: "none", label: "None (not recommended)" }]} />
        <KeyField label="Port" name="SMTP_PORT" hint={form.SMTP_SECURITY === "ssl" ? "465" : "587"} {...p} testid="system-smtp-port" />
        <KeyField label="Username" name="SMTP_USERNAME" {...p} testid="system-smtp-username" />
        <KeyField label="Password" name="SMTP_PASSWORD" secret {...p} testid="system-smtp-password" />
        <KeyField label="Sender email" name="SMTP_FROM_EMAIL" hint="bookings@hostechsolutions.com" {...p} testid="system-smtp-from-email" />
        <KeyField label="Sender name" name="SMTP_FROM_NAME" hint="Hostech Solutions" {...p} testid="system-smtp-from-name" />
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <SaveButton busy={busy} onClick={() => save(false)} testid="system-smtp-save" />
        {data.configured && <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="system-smtp-remove"
          onClick={() => window.confirm("Remove the system mail server?") && save(true)}>Remove</button>}
      </div>
      {data.configured && <TestSend />}
    </Panel>
  );
};
